/**
 * @file css.ts
 * @description Сборка CSS-переменных и data-атрибутов оформления сайта
 * (phase19.16 Р1; db-design-r1 §3 «CSS: собирает buildSiteThemeCss() из
 * shared, инлайн `<style>:root{…}</style>`»).
 *
 * Витрина (apps/web) вставляет строку в корневой layout, кабинет — в iframe
 * предпросмотра. Все цвета, шрифты и формы сайта, корзины, чекаута, ЛК и
 * блога берутся только из этих переменных; старые классы (`chalet-green` …)
 * становятся их алиасами до contract-выпуска (фаза §7.6, Р1-4).
 *
 * Перед сборкой документ проходит `readSiteTheme`: строка уходит в инлайн-
 * `<style>`, поэтому в неё попадают только проверенные значения (hex по
 * регулярке, шрифты из реестра, перечисления схемы) — внедрить CSS через
 * данные нельзя.
 *
 * ── Имена переменных (контракт с apps/web; менять только вместе) ──
 * Роли (SB8):
 *   --site-page-bg, --site-card-bg, --site-panel-bg, --site-text,
 *   --site-text-muted, --site-button, --site-accent, --site-dark-bg,
 *   --site-on-dark
 * Производные (SB9, derive.ts):
 *   --site-button-text, --site-button-hover, --site-button-hover-text,
 *   --site-accent-hover, --site-line, --site-line-on-dark, --site-focus-ring,
 *   --site-haze, --site-error, --site-error-bg, --site-success,
 *   --site-success-bg, --site-warning, --site-warning-bg
 * Шрифты (SB10):
 *   --site-font-heading, --site-font-body, --site-font-accent (без
 *   акцента — шрифт заголовков), --site-font-eyebrow
 * Типографика (SB73):
 *   --site-heading-weight, --site-heading-transform (none | uppercase),
 *   --site-eyebrow-tracking (`cssEm`: 0 | 0.08em …), --site-menu-transform,
 *   --site-heading-scale (множитель как есть — «Размер заголовков», SB10, SB120)
 * Формы (SB11, числами с SB120 этапа 2; формат чисел — `cssPx`: 0 → «0»,
 * иначе «<n>px», как у прежних таблиц):
 *   --site-button-radius (0 | 10px | … | 9999px у «Пилюли»),
 *   --site-button-transform, --site-radius (карточки, фото),
 *   --site-radius-control (поля, бейджи), --site-card-shadow (`cardShadowCss`:
 *   50 % = `SITE_CARD_SHADOW`, 0 — none, 100 — заметно плотнее, длины ×1,5),
 *   --site-card-border (толщина 0 — none; иначе «<w>px solid <цвет>», цвет —
 *   роль/свой или `var(--site-line)`)
 * Фон страницы (решение 2026-10-06, `backdrop.image`):
 *   --site-backdrop-image-size (cover | auto), --site-backdrop-image-repeat
 *   (no-repeat | repeat), --site-backdrop-image-attachment (scroll | fixed),
 *   --site-backdrop-image-opacity (0.1–1), --site-backdrop-image-dim (0–0.6,
 *   затемнение). Адрес картинки — витрина по mediaId (`/api/v1/public/content/media/<id>`).
 * Рамка (SB89):
 *   --site-frame-max-width, --site-frame-shadow (`frameShadowCss`: 50 % —
 *   прежняя тень рамки; 0 или «во всю ширину» — none)
 * Свечение: --site-haze — цвет свечения (нет — акцент) × сила %; при силе 0
 *   не используется (`data-site-haze="false"`).
 * Рисунки фона — не переменные: место и ширину по устройствам считает
 *   `backdropImageBox` (данные site-config, витрина ставит style).
 * Карточка товара (SB31, «Вид карточки» 2026-10-11) — отдельно от темы,
 *   из параметров каталога (`productCardCssVars`): --site-product-card-radius,
 *   --site-product-card-shadow, --site-product-card-border. «Как у всех» —
 *   ссылки на --site-radius / --site-card-shadow / --site-card-border.
 * Шапка и подвал (SB8, заливка 2026-10-11, surface-fill.ts):
 *   --site-header-bg, --site-footer-bg — заливка: роль — `var(--site-<роль>)`
 *   (живёт вместе с цветами темы), свой цвет — hex; --site-header-fg,
 *   --site-footer-fg — текст по тону заливки (`var(--site-text)` или
 *   `var(--site-on-dark)`). У прозрачной шапки --site-header-bg — вид
 *   после прокрутки и на страницах без фото.
 * Отступы (фаза §7.13, `theme.spacing.density` — множитель 0,5–1,6 с SB120 этапа 2):
 *   --site-space-scale — множитель как есть (v1 compact 0.8 | normal 1 | spacious 1.25);
 *   --site-space-xs / -s / -m / -l / -xl — 8 / 16 / 32 / 64 / 96 px × scale.
 *   Это значения для ПК: на телефоне витрина сжимает ступени своим
 *   медиа-запросом на долю --site-phone-factor.
 *   --site-phone-factor — доля отступов на телефоне (`spacing.phoneFactor`,
 *   SB120 этап 1; 0,6 по умолчанию). Витрина ставит её в `--site-space-mobile`
 *   только до 768 px (globals.css); сам `--site-space-mobile` здесь не
 *   выставляется — иначе он перебил бы правило для планшета.
 * Корзина (SB120 этап 4, `theme.cart`, cart.ts; db-design-sb120-stage4 §3.1) —
 * в самом конце списка, прежние пары на местах:
 *   --site-cart-bg (заливка; «как шапка» — var(--site-header-bg)),
 *   --site-cart-fg (текст и цены строк; «Авто» — по тону заливки),
 *   --site-cart-line (линии −/+ по тону), --site-cart-summary-bg,
 *   --site-cart-summary-fg, --site-cart-total (сумма), --site-cart-overlay
 *   (цвет подложки как есть), --site-cart-dim / --site-cart-pop-dim (доля
 *   0–0,8 у панели и у окна), --site-cart-radius, --site-cart-radius-control
 *   (¾ скругления; «как у сайта» — calc(var(--site-radius) * 0.75)),
 *   --site-cart-photo, --site-cart-w-phone, --site-cart-w-wide,
 *   --site-cart-pop-w (px), --site-cart-pop-rows (число строк окна).
 *   CSS дизайна страницы их не выводит (`omitPrefixes: ["--site-cart-"]`).
 *
 * ── Data-атрибуты (`siteThemeDataAttributes`, ставятся на `<html>`) ──
 *   data-site-header-logo     left | center | right | above   (SB90)
 *   data-site-header-tone     light | dark | transparent      (SB13)
 *   data-site-header-brand    logo | logo+name | logo+name+tagline | name
 *   data-site-header-split    число пунктов слева от логотипа (только center)
 *   data-site-header-sticky   true | false
 *   data-site-header-oversize true | false (только center)
 *   data-site-footer          row | columns                   (SB14)
 *   data-site-footer-tone     light | dark
 *   data-site-button-arrow    true | false
 *   data-site-frame           full | boxed
 *   data-site-backdrop        off | images
 *   data-site-haze            true | false (сила свечения > 0)
 *   data-site-page-bg         none | cover | tile | fixed     (картинка «Фона страницы»)
 *   data-site-page-bg-mobile  true | false (показывать ли на телефоне)
 *   data-site-cart            drawer | popover                (SB120 этап 4)
 *   data-site-cart-side       left | right (сторона панели)
 * `data-site-button-shape` и `data-site-cards` сняты в SB120 этапе 2: в CSS
 * витрины не использовались, форма и вид — числами в переменных.
 * Рисунки задника и показ частей подвала — данные (site-config), не CSS.
 *
 * ── Файлы шрифтов ── `siteThemeFontFaces` / `buildSiteFontFaceCss`: только
 * нужные начертания, адреса каталога по соглашению `siteFontFileUrl` (см.
 * ниже). Свои шрифты магазина (SB99, фаза §7.12, Р1-7) приходят через
 * реестр (`SiteFontRegistry`, fonts.ts): в CSS у них семейство `cf-<id>`,
 * файлы — по адресам из реестра, без `unicode-range`. Имя шрифта, которое
 * ввёл магазин, в CSS не попадает.
 */

import { deriveSiteColors } from "./derive.js";
import {
  fontStack,
  isSafeFontFileUrl,
  isShopFontEntry,
  nearestFontWeight,
  PLATFORM_FONT_REGISTRY,
  type ShopFontEntry,
  type ShopFontStyle,
  type SiteFontRegistry,
} from "./fonts.js";
import { SITE_COLOR_ROLES, siteColorVar, type SiteColors } from "./keys.js";
import type { SiteCatalogOptions } from "./catalog-options.js";
import { colorRefCss } from "./color-ref.js";
import { DENSITY_V1, HEADING_SCALE_V1, type SiteHeadingScale, type SiteSpacingDensityV1 } from "./legacy-v1.js";
import {
  readSiteTheme,
  type SiteBackdropImage,
  type SiteButtonRadius,
  type SiteCards,
  type SiteTheme,
} from "./schema.js";
import { resolveSurfaceFill, type SiteSurfaceFill } from "./surface-fill.js";
import { cartEffectiveFill } from "./cart.js";
import type { SiteAutoTextColor } from "./text-color.js";
import { SITE_BACKDROP_BREAKPOINTS, SITE_BACKDROP_SIZE_SHARE, SITE_THEME_LIMITS } from "./theme-limits.js";

/** Как получить стек `font-family` по ключу каталога. */
export type SiteFontResolver = (key: string) => string;

/**
 * Откуда брать шрифты при сборке переменных: реестр магазина (каталог +
 * свои шрифты, SB99) или — по-старому — функция стека. С функцией документ
 * читается по каталогу платформы: свои шрифты `shop:` станут системными.
 */
export type SiteThemeFonts = SiteFontRegistry | SiteFontResolver;

/** Параметры сборки. */
export interface SiteThemeCssOptions {
  /** Селектор блока переменных; по умолчанию `:root`. Предпросмотр может сузить. */
  selector?: string;
  /**
   * Префиксы переменных, которые не выводить (SB120 этап 4, §3.3): CSS
   * дизайна страницы (SB95б) собирается без `--site-cart-` — корзина одна на
   * сайт, живёт в layout и настраивается в одном месте. Нет — выводятся все.
   */
  omitPrefixes?: readonly string[];
}

/**
 * Переменные карточки товара по «Виду карточки» (SB31, 2026-10-11). Тема не
 * нужна: «как у всех» ссылается на живые переменные темы (кабинет меняет их
 * без пересборки), «свои» — те же функции, что у темы (скругление карточки
 * px, `cardShadowCss`, `cardBorderCss`). Витрина ставит их на корень вместе
 * с переменными темы; карточки каталога, «Похожих товаров» и элемента
 * «Товары» читают только --site-product-card-*.
 */
export function productCardCssVars(options: Pick<SiteCatalogOptions, "card">): Array<[string, string]> {
  const look = options.card.look;
  if (look.mode === "site") {
    return [
      ["--site-product-card-radius", "var(--site-radius)"],
      ["--site-product-card-shadow", "var(--site-card-shadow)"],
      ["--site-product-card-border", "var(--site-card-border)"],
    ];
  }
  return [
    ["--site-product-card-radius", cssPx(look.radius)],
    ["--site-product-card-shadow", cardShadowCss(look.cards.shadow)],
    ["--site-product-card-border", cardBorderCss(look.cards.border)],
  ];
}

/**
 * Значение переменной заливки: роль — живой `var(--site-…)`; свой цвет —
 * непрозрачный фон из `resolveSurfaceFill` (полупрозрачный #rrggbbaa уже
 * наложен на фон страницы): тон текста посчитан по нему же, и шапка не
 * просвечивает содержимым при прокрутке (ревью п. 6, 2026-10-11).
 */
export function surfaceFillCssValue(fill: SiteSurfaceFill, theme: { readonly colors: SiteColors }): string {
  return fill.kind === "custom" ? resolveSurfaceFill(fill, theme).background : `var(${siteColorVar(fill.role)})`;
}

// ── Числа → CSS (SB120 этап 2, db-design-sb120-stage2 §3) ──

/**
 * Длина в px для CSS: 0 → «0», иначе «<n>px». Формат прежних таблиц
 * (`RADIUS.none`, `BUTTON_RADIUS.square` давали «0») — так переменные
 * документа v1 и поднятого v2 совпадают побайтно.
 */
export function cssPx(value: number): string {
  return value === 0 ? "0" : `${value}px`;
}

/** Длина в em: 0 → «0», иначе «<n>em» (разрядка надписей капсом: 0.08em). */
export function cssEm(value: number): string {
  return value === 0 ? "0" : `${value}em`;
}

/** Скругление кнопок: «Пилюля» — 9999px (по высоте кнопки), иначе px. */
export function buttonRadiusCss(radius: SiteButtonRadius): string {
  return radius === "pill" ? "9999px" : cssPx(radius);
}

/**
 * Тень карточек «С тенью» (`--site-card-shadow` при 50 %). Экспорт — для
 * витрины (SB120 этап 1): тень подложки секции в % считается от неё.
 */
export const SITE_CARD_SHADOW = "0 1px 2px rgb(0 0 0 / 0.06), 0 8px 24px rgb(0 0 0 / 0.08)";
/** Тень рамки сайта при 50 % (прежняя `frame.shadow: true`). */
export const SITE_FRAME_SHADOW = "0 0 48px rgb(0 0 0 / 0.12)";

/**
 * Тень по шкале %: 0 — нет; 50 — эталонная тень (строка побайтно прежняя);
 * 100 — заметно плотнее: прозрачность слоёв ×3,5 (не выше 1), длины ×1,5.
 * До 50 меняется только прозрачность (× percent/50); выше 50 — прозрачность
 * × (1 + (percent − 50)/50 × 2,5) и длины `<n>px` × (1 + (percent − 50)/100)
 * с округлением до целых px (прежняя шкала «100 = вдвое» почти не видна на
 * светлом фоне — замечание владельца 2026-10-19). Прозрачность — до 0,001.
 * Одна шкала для тени карточек темы, подложки секции и рамки сайта
 * (перенесено из витрины section-frame.ts, SB120 этап 2).
 */
function scaledShadow(base: string, percent: number): string {
  if (percent <= 0) return "none";
  // Выше 50 — усиленная ветка: плотнее и шире
  const over = Math.max(0, percent - 50);
  const alphaK = over === 0 ? percent / 50 : 1 + (over / 50) * 2.5;
  const withAlpha = base.replace(/\/ ([\d.]+)\)/g, (_m, alpha: string) => {
    const scaled = Math.min(1, Math.round(Number(alpha) * alphaK * 1000) / 1000);
    return `/ ${scaled})`;
  });
  if (over === 0) return withAlpha;
  // Длины слоёв (смещение и размытие); нули без «px» не трогаем
  const lengthK = 1 + over / 100;
  return withAlpha.replace(/(\d+(?:\.\d+)?)px/g, (_m, len: string) => `${Math.round(Number(len) * lengthK)}px`);
}

/** Тень карточек и подложки секции в %: 50 = `SITE_CARD_SHADOW`. */
export function cardShadowCss(percent: number): string {
  return scaledShadow(SITE_CARD_SHADOW, percent);
}

/** Тень рамки сайта в %: 50 = `SITE_FRAME_SHADOW`. */
export function frameShadowCss(percent: number): string {
  return scaledShadow(SITE_FRAME_SHADOW, percent);
}

/**
 * Рамка карточек: толщина 0 — none; иначе «<w>px solid <цвет>», цвет — роль
 * (живой `var(--site-<роль>)`) или свой; нет цвета — линия сайта (при 1 px
 * без цвета — прежняя «1px solid var(--site-line)»).
 */
export function cardBorderCss(border: SiteCards["border"]): string {
  if (border.width <= 0) return "none";
  return `${border.width}px solid ${border.color ? colorRefCss(border.color) : "var(--site-line)"}`;
}

/**
 * @deprecated SB120 этап 2: размер заголовков — число `type.headingScale`, в CSS
 * идёт как есть. Таблица ступеней v1 — для подсказок кабинета до его перевода.
 */
export const HEADING_SCALE_FACTORS: Readonly<Record<SiteHeadingScale, string>> = {
  s: String(HEADING_SCALE_V1.s),
  m: String(HEADING_SCALE_V1.m),
  l: String(HEADING_SCALE_V1.l),
};

/**
 * @deprecated SB120 этап 2: плотность — число `spacing.density`, оно же
 * `--site-space-scale`. Таблица ступеней v1 — `DENSITY_V1` (legacy-v1.ts).
 */
export const SITE_SPACE_SCALE: Readonly<Record<SiteSpacingDensityV1, number>> = DENSITY_V1;

/** Ступени шкалы отступов, px при множителе 1 (имя ступени → `--site-space-<имя>`). */
export const SITE_SPACE_STEPS = { xs: 8, s: 16, m: 32, l: 64, xl: 96 } as const;

/**
 * Сжатие ступеней на телефоне по умолчанию — применяет витрина медиа-запросом.
 * С SB120 этап 1 — из темы (`spacing.phoneFactor` → `--site-phone-factor`).
 */
export const SITE_SPACE_MOBILE_FACTOR = 0.6;

// ── Рисунки фона (SB120 этап 2, db-design-sb120-stage2 §3) ──

/** Устройство для рисунков фона: телефон < 768, планшет 768–1023, ПК ≥ 1024. */
export type SiteBackdropDevice = "phone" | "tablet" | "desktop";

/** Место и вид рисунка фона на устройстве — готовые значения для `style`. */
export interface SiteBackdropImageBox {
  /** Точка привязки по горизонтали, % ширины области рисунков. */
  left: string;
  /** Точка привязки по вертикали, % высоты области рисунков. */
  top: string;
  /** Сдвиг рисунка на себя: `translate(−x%, −y%)` — у 100/100 правый нижний угол рисунка в точке. */
  transform: string;
  /** Ширина, px. */
  width: number;
  /** Прозрачность 0–1. */
  opacity: number;
}

/** Ширина рисунка на устройстве: ПК — `size`; планшет и телефон — своё число или доля ПК. */
export function backdropImageWidth(
  image: Pick<SiteBackdropImage, "size" | "sizeTablet" | "sizePhone">,
  device: SiteBackdropDevice,
): number {
  if (device === "desktop") return image.size;
  if (device === "tablet") return image.sizeTablet ?? Math.round(image.size * SITE_BACKDROP_SIZE_SHARE.tablet);
  return image.sizePhone ?? Math.round(image.size * SITE_BACKDROP_SIZE_SHARE.phone);
}

/**
 * Геометрия рисунка фона. Область — окно без `SITE_BACKDROP_AREA` (96 px
 * сверху, 48 снизу): витрина ставит слой `position: fixed` с такими отступами,
 * рисунок внутри — `left/top` и `transform` отсюда. Для 0/0, 100/0, 0/100,
 * 100/100 это ровно прежние углы (`left-0 top-24` … `right-0 bottom-12`).
 * Яркость без ключа — как было: 30 % на телефоне, 35 % с 768.
 */
export function backdropImageBox(image: SiteBackdropImage, device: SiteBackdropDevice): SiteBackdropImageBox {
  const fallback = device === "phone" ? SITE_THEME_LIMITS.backdropOpacity.phone : SITE_THEME_LIMITS.backdropOpacity.wide;
  return {
    left: `${image.x}%`,
    top: `${image.y}%`,
    // `${-0}` — «0»: у угла 0/0 сдвиг «translate(0%, 0%)».
    transform: `translate(${-image.x}%, ${-image.y}%)`,
    width: backdropImageWidth(image, device),
    opacity: (image.opacity ?? fallback) / 100,
  };
}

/**
 * `sizes` для srcset рисунка: ширина показа по устройствам (порядок и формат
 * — как прежняя таблица SIZES SiteBackdrop.tsx).
 */
export function backdropImageSizes(image: Pick<SiteBackdropImage, "size" | "sizeTablet" | "sizePhone">): string {
  const { tablet, desktop } = SITE_BACKDROP_BREAKPOINTS;
  return (
    `(min-width: ${desktop}px) ${backdropImageWidth(image, "desktop")}px, ` +
    `(min-width: ${tablet}px) ${backdropImageWidth(image, "tablet")}px, ` +
    `${backdropImageWidth(image, "phone")}px`
  );
}

const transform = (value: "asis" | "upper"): string => (value === "upper" ? "uppercase" : "none");

/**
 * Пары «переменная → значение» — отдельно от строки, чтобы кабинет мог
 * выставлять их через `style.setProperty` без пересборки `<style>`.
 */
export function buildSiteThemeVars(
  input: SiteTheme,
  fonts: SiteThemeFonts = PLATFORM_FONT_REGISTRY,
): Array<[string, string]> {
  const registry = typeof fonts === "function" ? PLATFORM_FONT_REGISTRY : fonts;
  const stack: SiteFontResolver =
    typeof fonts === "function" ? fonts : (key) => fontStack(key, registry);
  const theme = readSiteTheme(input, undefined, undefined, registry).theme;
  const { colors } = theme;
  const derived = deriveSiteColors(colors, theme.backdrop.haze);
  const vars: Array<[string, string]> = [];

  for (const role of SITE_COLOR_ROLES) vars.push([siteColorVar(role), colors[role]]);

  vars.push(
    ["--site-button-text", derived.buttonText],
    ["--site-button-hover", derived.buttonHover],
    ["--site-button-hover-text", derived.buttonHoverText],
    ["--site-accent-hover", derived.accentHover],
    ["--site-line", derived.line],
    ["--site-line-on-dark", derived.lineOnDark],
    ["--site-focus-ring", derived.focusRing],
    ["--site-haze", derived.haze],
    ["--site-error", derived.status.error.fg],
    ["--site-error-bg", derived.status.error.bg],
    ["--site-success", derived.status.success.fg],
    ["--site-success-bg", derived.status.success.bg],
    ["--site-warning", derived.status.warning.fg],
    ["--site-warning-bg", derived.status.warning.bg],
  );

  const accentKey = theme.fonts.accent ?? theme.fonts.heading;
  vars.push(
    ["--site-font-heading", stack(theme.fonts.heading)],
    ["--site-font-body", stack(theme.fonts.body)],
    ["--site-font-accent", stack(accentKey)],
    [
      "--site-font-eyebrow",
      theme.type.eyebrowFrom === "heading" ? "var(--site-font-heading)" : "var(--site-font-body)",
    ],
    [
      "--site-heading-weight",
      String(nearestFontWeight(theme.fonts.heading, theme.type.headingWeight, registry)),
    ],
    ["--site-heading-transform", transform(theme.type.headingCase)],
    ["--site-heading-scale", String(theme.type.headingScale)],
    ["--site-eyebrow-tracking", cssEm(theme.type.eyebrowTracking)],
    ["--site-menu-transform", transform(theme.header.menuCase)],
  );

  // Заливка шапки и подвала (SB8, 2026-10-11); текст — по тону заливки.
  const fg = (tone: "light" | "dark"): string => (tone === "dark" ? "var(--site-on-dark)" : "var(--site-text)");
  vars.push(
    ["--site-header-bg", surfaceFillCssValue(theme.header.fill, theme)],
    ["--site-header-fg", fg(resolveSurfaceFill(theme.header.fill, theme).tone)],
    ["--site-footer-bg", surfaceFillCssValue(theme.footer.fill, theme)],
    ["--site-footer-fg", fg(resolveSurfaceFill(theme.footer.fill, theme).tone)],
  );

  const { radius, cards } = theme.shapes;
  const boxed = theme.frame.mode === "boxed";
  vars.push(
    ["--site-button-radius", buttonRadiusCss(theme.shapes.buttonRadius)],
    ["--site-button-transform", transform(theme.shapes.buttonCase)],
    ["--site-radius", cssPx(radius.card)],
    ["--site-radius-control", cssPx(radius.field)],
    ["--site-card-shadow", cardShadowCss(cards.shadow)],
    ["--site-card-border", cardBorderCss(cards.border)],
    ["--site-frame-max-width", boxed ? `${theme.frame.maxWidth}px` : "none"],
    // «Во всю ширину» — рамки нет, тени тоже (как было).
    ["--site-frame-shadow", boxed ? frameShadowCss(theme.frame.shadow) : "none"],
  );

  // «Фон страницы» (решение 2026-10-06): адрес картинки витрина собирает
  // сама по mediaId (стабильный адрес медиатеки), здесь — как её класть.
  const bg = theme.backdrop.image;
  vars.push(
    ["--site-backdrop-image-size", bg === null ? "auto" : bg.mode === "tile" ? "auto" : "cover"],
    ["--site-backdrop-image-repeat", bg !== null && bg.mode === "tile" ? "repeat" : "no-repeat"],
    ["--site-backdrop-image-attachment", bg !== null && bg.mode === "fixed" ? "fixed" : "scroll"],
    ["--site-backdrop-image-opacity", String(bg?.opacity ?? 1)],
    ["--site-backdrop-image-dim", String(bg?.dim ?? 0)],
  );

  const scale = theme.spacing.density;
  vars.push(["--site-space-scale", String(scale)]);
  for (const [step, px] of Object.entries(SITE_SPACE_STEPS)) {
    // Округление до 0,1 px: 0.8 × 8 = 6.4 без хвостов плавающей точки.
    vars.push([`--site-space-${step}`, `${Math.round(px * scale * 10) / 10}px`]);
  }
  vars.push(["--site-phone-factor", String(theme.spacing.phoneFactor)]);
  // Корзина (SB120 этап 4) — строго в конце: прежние пары остаются на своих
  // местах побайтно (золотой тест cart.golden.test.ts).
  vars.push(...cartCssVars(theme, fg));
  return vars;
}

/** Цвет текста корзины: «Авто» — по тону заливки, роль — живая переменная, свой — hex. */
function cartTextCss(color: SiteAutoTextColor, autoFg: string): string {
  if (color.kind === "auto") return autoFg;
  return colorRefCss(color);
}

/**
 * Переменные корзины (db-design-sb120-stage4 §3.1). Заливка «как шапка»
 * (`fill: null`) ссылается на `--site-header-bg`, тон — по заливке шапки.
 * Подложка — роль или свой цвет как есть: альфа подложки не кладётся на фон
 * страницы (под ней — сама страница). Скругление «как у сайта» — от
 * `--site-radius`, у кнопок −/+ доля ¾ (8 → 6, как было).
 */
function cartCssVars(theme: SiteTheme, fg: (tone: "light" | "dark") => string): Array<[string, string]> {
  const { cart } = theme;
  const tone = resolveSurfaceFill(cartEffectiveFill(cart, theme), theme).tone;
  const summaryTone = resolveSurfaceFill(cart.summaryFill, theme).tone;
  const share = SITE_THEME_LIMITS.cart.radius.controlShare;
  return [
    ["--site-cart-bg", cart.fill === null ? "var(--site-header-bg)" : surfaceFillCssValue(cart.fill, theme)],
    ["--site-cart-fg", cartTextCss(cart.text, fg(tone))],
    ["--site-cart-line", tone === "dark" ? "var(--site-line-on-dark)" : "var(--site-line)"],
    ["--site-cart-summary-bg", surfaceFillCssValue(cart.summaryFill, theme)],
    ["--site-cart-summary-fg", fg(summaryTone)],
    ["--site-cart-total", cartTextCss(cart.totalColor, fg(summaryTone))],
    ["--site-cart-overlay", colorRefCss(cart.overlayColor)],
    ["--site-cart-dim", String(cart.drawer.dim / 100)],
    ["--site-cart-pop-dim", String(cart.popover.dim / 100)],
    ["--site-cart-radius", cart.radius === null ? "var(--site-radius)" : cssPx(cart.radius)],
    [
      "--site-cart-radius-control",
      cart.radius === null ? `calc(var(--site-radius) * ${share})` : cssPx(Math.round(cart.radius * share)),
    ],
    ["--site-cart-photo", `${cart.photo}px`],
    ["--site-cart-w-phone", `${cart.drawer.widthPhone}px`],
    ["--site-cart-w-wide", `${cart.drawer.widthWide}px`],
    ["--site-cart-pop-w", `${cart.popover.width}px`],
    ["--site-cart-pop-rows", String(cart.popover.rows)],
  ];
}

/**
 * Строка для инлайн-`<style>`: `:root{--site-page-bg:#ffffff;…}`.
 * `fonts` — реестр шрифтов магазина (каталог платформы + свои шрифты, SB99;
 * по умолчанию только каталог) или функция стека по ключу (web может
 * подставить имена своих `@font-face`, если они отличаются).
 */
export function buildSiteThemeCss(
  theme: SiteTheme,
  fonts: SiteThemeFonts = PLATFORM_FONT_REGISTRY,
  options: SiteThemeCssOptions = {},
): string {
  const selector = options.selector ?? ":root";
  const omit = options.omitPrefixes ?? [];
  const body = buildSiteThemeVars(theme, fonts)
    .filter(([name]) => !omit.some((prefix) => name.startsWith(prefix)))
    .map(([name, value]) => `${name}:${value};`)
    .join("");
  return `${selector}{${body}}`;
}

// ── Файлы шрифтов ──
// Соглашение об адресах (предложение для Р1-4, согласовать с web-storefront и
// devops): web кладёт в образ файлы пакетов `@fontsource/<key>/files/*` в
// `public/fonts/<key>/`; имена файлов — как в пакете. Сервер в site-config и
// web в `@font-face` строят адреса этой функцией, поэтому расходиться им нечем.

/** Наборы знаков, которые раздаются: кириллица и латиница (цифры, знаки). */
export const SITE_FONT_SUBSETS = {
  cyrillic: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
  latin:
    "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
} as const;

export type SiteFontSubset = keyof typeof SITE_FONT_SUBSETS;

/** Адрес файла начертания: `/fonts/lora/lora-cyrillic-700-normal.woff2`. */
export function siteFontFileUrl(
  key: string,
  subset: SiteFontSubset,
  weight: number,
  base = "/fonts",
): string {
  return `${base}/${key}/${key}-${subset}-${weight}-normal.woff2`;
}

/** Одно правило `@font-face`. */
export interface SiteFontFace {
  key: string;
  /** Имя семейства в CSS: у каталога — имя шрифта, у своего — `cf-<id>`. */
  family: string;
  weight: number;
  style: ShopFontStyle;
  /** Набор знаков каталога; у своего шрифта `null` — файл целиком. */
  subset: SiteFontSubset | null;
  /** `null` — правило без `unicode-range` (свой шрифт). */
  unicodeRange: string | null;
  url: string;
}

/** Параметры `siteThemeFontFaces`. */
export interface SiteThemeFontFacesOptions {
  /** Корень файлов каталога платформы; по умолчанию `/fonts` (свой домен). */
  base?: string;
  /** Шрифты магазина (SB99); по умолчанию — только каталог платформы. */
  registry?: SiteFontRegistry;
}

/**
 * Какие файлы нужны оформлению: заголовки — одно начертание (ближайшее к
 * выбранному), текст — обычное и жирное, акцент — обычное. Только
 * нужные файлы: каждый лишний woff2 — задержка первого экрана на телефоне.
 * Системный шрифт файлов не имеет.
 *
 * Свой шрифт магазина (SB99): лица строятся из его `files` (адрес как есть,
 * без деления на наборы знаков), семейство — `cf-<id>`. На каждый нужный вес
 * отдаются и прямое, и курсивное начертание, если загружены: браузер
 * скачивает курсив только когда он встретился на странице. Жирного файла
 * нет — у текста остаётся только 400 (nearestFontWeight).
 */
export function siteThemeFontFaces(
  input: SiteTheme,
  options: SiteThemeFontFacesOptions = {},
): SiteFontFace[] {
  const base = options.base ?? "/fonts";
  const registry = options.registry ?? PLATFORM_FONT_REGISTRY;
  const theme = readSiteTheme(input, undefined, undefined, registry).theme;
  const nearest = (key: string, weight: number): number => nearestFontWeight(key, weight, registry);
  const wanted: Array<[string, number]> = [
    [theme.fonts.heading, nearest(theme.fonts.heading, theme.type.headingWeight)],
    [theme.fonts.body, nearest(theme.fonts.body, 400)],
    [theme.fonts.body, nearest(theme.fonts.body, 700)],
  ];
  if (theme.fonts.accent !== null) {
    wanted.push([theme.fonts.accent, nearest(theme.fonts.accent, 400)]);
  }
  const seen = new Set<string>();
  const faces: SiteFontFace[] = [];
  for (const [key, weight] of wanted) {
    const entry = registry.find(key);
    if (!entry || seen.has(`${key}:${weight}`)) continue;
    seen.add(`${key}:${weight}`);
    if (isShopFontEntry(entry)) {
      faces.push(...shopFontFaces(entry, weight));
      continue;
    }
    if (entry.license === "system") continue;
    for (const subset of Object.keys(SITE_FONT_SUBSETS) as SiteFontSubset[]) {
      faces.push({
        key,
        family: entry.family,
        weight,
        style: "normal",
        subset,
        unicodeRange: SITE_FONT_SUBSETS[subset],
        url: siteFontFileUrl(key, subset, weight, base),
      });
    }
  }
  return faces;
}

/** Лица своего шрифта для одного веса: прямое первым, затем курсив. */
function shopFontFaces(entry: ShopFontEntry, weight: number): SiteFontFace[] {
  return entry.files
    .filter((file) => file.weight === weight)
    .sort((a, b) => Number(a.style === "italic") - Number(b.style === "italic"))
    .map((file) => ({
      key: entry.key,
      family: entry.cssFamily,
      weight: file.weight,
      style: file.style,
      subset: null,
      unicodeRange: null,
      url: file.url,
    }));
}

/**
 * Что можно писать в `@font-face` инлайн-`<style>`. Лица обычно строит
 * `siteThemeFontFaces`, но массив мог прийти извне (ответ сервера, канва):
 * правило с подозрительным семейством или адресом не выводится вовсе —
 * через данные нельзя закрыть `@font-face` и дописать своё правило.
 */
const SAFE_FACE_FAMILY = /^[A-Za-z0-9 _-]+$/;
const SAFE_UNICODE_RANGE = /^[U+0-9A-Fa-f, -]+$/;

function isSafeFace(face: SiteFontFace): boolean {
  return (
    SAFE_FACE_FAMILY.test(face.family) &&
    isSafeFontFileUrl(face.url) &&
    Number.isInteger(face.weight) &&
    (face.style === "normal" || face.style === "italic") &&
    (face.unicodeRange === null || SAFE_UNICODE_RANGE.test(face.unicodeRange))
  );
}

/**
 * Правила `@font-face` для инлайн-`<style>` (font-display: swap — текст виден
 * сразу). `unicode-range` пишется только у файлов каталога (поделены на
 * наборы знаков); свой шрифт — один файл на начертание, без него.
 */
export function buildSiteFontFaceCss(faces: readonly SiteFontFace[]): string {
  return faces
    .filter(isSafeFace)
    .map(
      (face) =>
        `@font-face{font-family:"${face.family}";font-style:${face.style};font-weight:${face.weight};` +
        `font-display:swap;src:url("${face.url}") format("woff2");` +
        (face.unicodeRange === null ? "}" : `unicode-range:${face.unicodeRange};}`),
    )
    .join("");
}

/** Data-атрибуты шаблонов шапки, подвала, кнопок, рамки и задника (список — в шапке файла). */
export function siteThemeDataAttributes(input: SiteTheme): Record<string, string> {
  const theme = readSiteTheme(input).theme;
  const { header, footer, shapes, frame, backdrop, cart } = theme;
  const attrs: Record<string, string> = {
    "data-site-header-logo": header.logoPosition,
    "data-site-header-tone": header.tone,
    "data-site-header-brand": header.brandMode,
    "data-site-header-sticky": String(header.sticky),
    "data-site-header-oversize": String(header.logoPosition === "center" && header.logoOversize),
    "data-site-footer": footer.template,
    "data-site-footer-tone": footer.tone,
    "data-site-button-arrow": String(shapes.buttonArrow),
    "data-site-frame": frame.mode,
    "data-site-backdrop": backdrop.mode,
    // globals.css рисует пятна свечения только при «true»: сила 0 — свечения нет.
    "data-site-haze": String(backdrop.haze.strength > 0),
    "data-site-page-bg": backdrop.image === null ? "none" : backdrop.image.mode,
    "data-site-page-bg-mobile": String(backdrop.image !== null && !backdrop.image.hideOnMobile),
    // Вид корзины и сторона панели (SB120 этап 4): раскладку выбирает CSS витрины.
    "data-site-cart": cart.mode,
    "data-site-cart-side": cart.drawer.side,
  };
  if (header.logoPosition === "center" && header.splitAt !== null) {
    attrs["data-site-header-split"] = String(header.splitAt);
  }
  return attrs;
}
